import { useEffect, useState } from "react";
import { Check, Pencil, Sparkles } from "lucide-react";
import { Subscriptions, type CatalogTier } from "../../lib/apiTyped";
import { fmtEur, tierDisplayName } from "../../lib/format";
import { EditTierModal } from "../admin/EditTierModal";
import { Card } from "./Card";
import { Spinner } from "./Spinner";

// CTAs go to a mailto so any account (admin or owner) can request a plan
// change without us having to ship a forms backend. Subject is prefilled
// with the requesting context so I can triage at a glance.
const CONTACT_EMAIL = "hello@cowboys.so";

type Props = {
  // Highlights the tile matching this slug (e.g. the org's current tier).
  currentTierSlug?: string | null;
  // Hides CTAs (admin uses this for an info-only catalog view).
  readOnly?: boolean;
  // Admin mode: each tier card gets an "Editar" button that opens the
  // EditTierModal. Mutually exclusive with readOnly in practice.
  editable?: boolean;
  // Used to prefill the contact email subject so admin can identify the
  // sender's org without digging through Firestore.
  contactContext?: { orgId?: string; orgName?: string; email?: string };
};

export function PlansShowcase({
  currentTierSlug,
  readOnly,
  editable,
  contactContext,
}: Props) {
  const [tiers, setTiers] = useState<CatalogTier[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [editing, setEditing] = useState<CatalogTier | null>(null);

  useEffect(() => {
    let cancelled = false;
    Subscriptions.catalog()
      .then((res) => {
        if (!cancelled) setTiers(res.tiers);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load catalog");
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  if (error) {
    return (
      <Card className="p-6">
        <p className="text-sm text-error">{error}</p>
      </Card>
    );
  }
  if (!tiers) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tiers.map((t) => (
          <PlanCard
            key={t.id}
            tier={t}
            isCurrent={t.slug === currentTierSlug}
            readOnly={readOnly}
            editable={editable}
            onEdit={() => setEditing(t)}
            contactContext={contactContext}
          />
        ))}
        <EnterprisePlanCard readOnly={readOnly} contactContext={contactContext} />
      </div>
      {editing && (
        <EditTierModal
          tier={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </>
  );
}

function PlanCard({
  tier,
  isCurrent,
  readOnly,
  editable,
  onEdit,
  contactContext,
}: {
  tier: CatalogTier;
  isCurrent: boolean;
  readOnly?: boolean;
  editable?: boolean;
  onEdit?: () => void;
  contactContext?: Props["contactContext"];
}) {
  const features: Array<[string, string]> = [
    [`${(tier.monthlyCreditsLimit / 1_000_000).toFixed(1)}M credits`, "/month"],
    [`${(tier.storageBytesLimit / 1_073_741_824).toFixed(0)} GB storage`, ""],
    [`${tier.requestsPerMinuteLimit} requests`, "/min"],
  ];

  return (
    <Card
      className={`p-6 flex flex-col gap-4 ${
        isCurrent ? "ring-2 ring-ink" : ""
      }`}
    >
      <div>
        <div className="flex items-center justify-between">
          <div className="text-lg font-black text-ink">
            {tierDisplayName(tier.slug, tier.name)}
          </div>
          {isCurrent && (
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-2 bg-bg px-2 py-0.5 rounded-full">
              Current
            </span>
          )}
        </div>
        <div className="mt-1 text-2xl font-black text-ink">
          {fmtEur(tier.pricePerSeatEur)}
          <span className="text-sm font-normal text-ink-2"> / seat / month</span>
        </div>
      </div>

      <ul className="space-y-2 text-sm text-ink-2 flex-1">
        {features.map(([label, suffix]) => (
          <li key={label} className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 text-ink shrink-0" />
            <span>
              {label}
              <span className="text-ink-3">{suffix}</span>
            </span>
          </li>
        ))}
      </ul>

      {editable ? (
        <button
          onClick={onEdit}
          className="inline-flex items-center justify-center gap-2 bg-ink text-white text-sm font-medium px-4 py-2 rounded-full hover:opacity-80 transition-opacity"
        >
          <Pencil className="w-3.5 h-3.5" />
          Edit
        </button>
      ) : (
        !readOnly &&
        !isCurrent && (
          <ContactButton
            subject={`Activate plan ${tierDisplayName(tier.slug, tier.name)}`}
            context={contactContext}
            tierSlug={tier.slug}
            label="Request this plan"
          />
        )
      )}
    </Card>
  );
}

function EnterprisePlanCard({
  readOnly,
  contactContext,
}: {
  readOnly?: boolean;
  contactContext?: Props["contactContext"];
}) {
  return (
    <Card className="p-6 flex flex-col gap-4 bg-gradient-to-br from-ink/5 to-transparent">
      <div>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-ink" />
          <div className="text-lg font-black text-ink">Enterprise</div>
        </div>
        <div className="mt-1 text-2xl font-black text-ink">Custom</div>
        <div className="text-sm text-ink-2">pricing based on needs</div>
      </div>

      <ul className="space-y-2 text-sm text-ink-2 flex-1">
        <li className="flex items-start gap-2">
          <Check className="w-4 h-4 mt-0.5 text-ink shrink-0" />
          <span>Custom volume and rate limits</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="w-4 h-4 mt-0.5 text-ink shrink-0" />
          <span>Priority support and SLA</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="w-4 h-4 mt-0.5 text-ink shrink-0" />
          <span>Custom billing and onboarding</span>
        </li>
      </ul>

      {!readOnly && (
        <ContactButton
          subject="Plan Enterprise"
          context={contactContext}
          label="Talk to sales"
        />
      )}
    </Card>
  );
}

function ContactButton({
  subject,
  context,
  tierSlug,
  label,
}: {
  subject: string;
  context?: Props["contactContext"];
  tierSlug?: string;
  label: string;
}) {
  const orgRef = context?.orgName
    ? `${context.orgName}${context.orgId ? ` (${context.orgId})` : ""}`
    : context?.orgId ?? "—";
  const fullSubject = `[Cowboy] ${subject}${
    context?.orgName ? ` — ${context.orgName}` : ""
  }`;
  const body = [
    `Hi,`,
    ``,
    tierSlug
      ? `I'd like to activate the ${tierDisplayName(tierSlug, tierSlug)} plan for my organization.`
      : `I need an Enterprise plan for my organization.`,
    ``,
    `Org: ${orgRef}`,
    context?.email ? `Email: ${context.email}` : "",
    ``,
    `Please let me know how to proceed.`,
    `Thank you.`,
  ]
    .filter(Boolean)
    .join("\n");
  const href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    fullSubject,
  )}&body=${encodeURIComponent(body)}`;
  return (
    <a
      href={href}
      className="inline-flex items-center justify-center bg-ink text-white text-sm font-medium px-4 py-2 rounded-full hover:opacity-80 transition-opacity"
    >
      {label}
    </a>
  );
}
